import { useState, useCallback } from 'react'
import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'

import type { ConsultationListItemState, MissionJsonType, StreamingChunk } from '@/api/model/chatType'
import {
  getUserConsultBlockListApi,
  createNewChatApi,
  continueConsultationApi,
  getConsultationListApi,
  getConsultationChatMessageListApi,
  getMyStepsByLatestBlockApi,
  getConsultationDetailApi,
  getActiveConsultationApi
} from '@/api/chatApi'

export const useUserConsultBlockList = (user_id: number) => {
  return useQuery({
    queryKey: ['userConsultBlockList', user_id],
    queryFn: () => getUserConsultBlockListApi(user_id)
  })
}

// 공통 스트리밍 상태 관리 훅
const useBaseStreamingChat = () => {
  const [isStreaming, setIsStreaming] = useState(false)
  const [consultationStarted, setConsultationStarted] = useState(false)
  const [streamedContent, setStreamedContent] = useState('')
  const [consultationId, setConsultationId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [abortController, setAbortController] = useState<AbortController | null>(null)
  const [suggestChitchat, setSuggestChitchat] = useState<boolean>(false)
  const [isCompletedForRating, setIsCompletedForRating] = useState<boolean>(false)
  const [isWaitingForStreaming, setIsWaitingForStreaming] = useState<boolean>(false)
  const [isStreamingEnd, setIsStreamingEnd] = useState<boolean>(false)
  const [missionJson, setMissionJson] = useState<MissionJsonType | null>(null)
  const [strategyJson, setStrategyJson] = useState<boolean>(false)

  const handleChunk = useCallback((chunk: StreamingChunk) => {
    // console.log('📦 받은 chunk:', chunk)

    if (chunk.type === 'consultation_started') {
      setIsWaitingForStreaming(true)
      setConsultationStarted(true)
      setStrategyJson(false)
    } else if (chunk.type === 'ai_response') {
      // content가 있고 완료되지 않은 경우에만 누적
      if (chunk.content && !chunk.is_complete) {
        setStreamedContent(prev => prev + chunk.content)
        setIsWaitingForStreaming(false)
      }
      setStrategyJson(false)
    } else if (chunk.type === 'consultation_response_completed') {
      setConsultationId(chunk.consultation_id || null)
      setIsWaitingForStreaming(false)
      setIsStreamingEnd(true)
      setStrategyJson(false)
    } else if (chunk.type === 'switch_suggestion') {
      setSuggestChitchat(true)
      setIsWaitingForStreaming(false)
      if (chunk.content) {
        setStreamedContent(prev => prev + chunk.content)
      }
    } else if (chunk.type === 'complete') {
      setConsultationStarted(false)
      setIsWaitingForStreaming(false)
      setIsCompletedForRating(true)
      setIsStreamingEnd(true)
      setStrategyJson(false)
    } else if (chunk.type === 'mission_suggest') {
      if (chunk.json) {
        setMissionJson(chunk.json)
      }
    } else if (chunk.type === 'strategy_creation') {
      setStrategyJson(true)
    } else if (chunk.type === 'error') {
      setError('오류가 발생했습니다.')
      setIsWaitingForStreaming(false)
      setIsStreamingEnd(true)
      setStrategyJson(false)
    }

    if (chunk.consultation_id) {
      setConsultationId(chunk.consultation_id)
    }
  }, [])

  const handleComplete = useCallback((finalConsultationId?: number) => {
    setIsStreaming(false)
    setIsStreamingEnd(true)
    setIsWaitingForStreaming(false)
    if (finalConsultationId) {
      setConsultationId(finalConsultationId)
    }
    console.log('✅ 스트리밍 완료, consultation_id:', finalConsultationId)
  }, [])

  const handleError = useCallback((error: Error) => {
    // AbortError는 스트리밍 중단이므로 에러로 처리하지 않음
    if (error.name === 'AbortError') {
      console.log('🛑 스트리밍 중단됨')
      setIsStreaming(false)
      return
    }

    console.log('❌ 스트리밍 오류:', error.message)
    handleComplete()
    setError(error.message)
    setIsStreaming(false)
    setIsStreamingEnd(true)
  }, [])

  const initializeStream = useCallback(() => {
    const controller = new AbortController()
    setAbortController(controller)
    setIsStreaming(true)
    setConsultationStarted(false)
    setStreamedContent('')
    setConsultationId(null)
    setError(null)
    return controller
  }, [])

  const cancelStream = useCallback(() => {
    if (abortController) {
      abortController.abort()
    }
  }, [abortController])

  const resetStream = useCallback(() => {
    setStreamedContent('')
    setConsultationStarted(false)
    setConsultationId(null)
    setError(null)
    setIsStreaming(false)
    setAbortController(null)
  }, [])

  return {
    isStreaming,
    consultationStarted,
    streamedContent,
    consultationId,
    error,
    handleChunk,
    handleComplete,
    handleError,
    initializeStream,
    cancelStream,
    resetStream,
    suggestChitchat,
    setSuggestChitchat,
    isCompletedForRating,
    setIsCompletedForRating,
    isWaitingForStreaming,
    isStreamingEnd,
    setIsStreamingEnd,
    missionJson,
    setMissionJson,
    strategyJson
  }
}

// (자유주제 상담) 상담 시작 및 대화방 생성 커스텀 훅
export const useCreateNewChat = () => {
  const baseHook = useBaseStreamingChat()
  const queryClient = useQueryClient()
  const createNewChat = useCallback(
    async (message?: string, block_id?: number) => {
      const controller = baseHook.initializeStream()

      await createNewChatApi(
        baseHook.handleChunk,
        baseHook.handleComplete,
        baseHook.handleError,
        message,
        block_id,
        controller.signal
      )
    },
    [baseHook]
  )

  if (baseHook.consultationId) {
    queryClient.invalidateQueries({ queryKey: ['consultationList'] })
    queryClient.invalidateQueries({ queryKey: ['activeConsultation'] })
  }

  return {
    createNewChat,
    isStreaming: baseHook.isStreaming,
    consultationStarted: baseHook.consultationStarted,
    streamedContent: baseHook.streamedContent,
    consultationId: baseHook.consultationId,
    error: baseHook.error,
    resetStream: baseHook.resetStream,
    cancelStream: baseHook.cancelStream,
    suggestChitchat: baseHook.suggestChitchat,
    setSuggestChitchat: baseHook.setSuggestChitchat,
    isCompletedForRating: baseHook.isCompletedForRating,
    setIsCompletedForRating: baseHook.setIsCompletedForRating,
    isWaitingForStreaming: baseHook.isWaitingForStreaming,
    isStreamingEnd: baseHook.isStreamingEnd,
    setIsStreamingEnd: baseHook.setIsStreamingEnd,
    missionJson: baseHook.missionJson,
    setMissionJson: baseHook.setMissionJson,
    strategyJson: baseHook.strategyJson
  }
}

// 대화 이어가기 (스트리밍 방식)
export const useContinueStreamingChat = () => {
  const baseHook = useBaseStreamingChat()

  const continueStreamingChat = useCallback(
    async (consultation_id: number, message: string) => {
      const controller = baseHook.initializeStream()

      await continueConsultationApi(
        consultation_id,
        message,
        baseHook.handleChunk,
        baseHook.handleComplete,
        baseHook.handleError,
        controller.signal
      )
    },
    [baseHook]
  )

  return {
    continueStreamingChat,
    isStreaming: baseHook.isStreaming,
    consultationStarted: baseHook.consultationStarted,
    streamedContent: baseHook.streamedContent,
    consultationId: baseHook.consultationId,
    error: baseHook.error,
    resetStream: baseHook.resetStream,
    cancelStream: baseHook.cancelStream,
    suggestChitchat: baseHook.suggestChitchat,
    setSuggestChitchat: baseHook.setSuggestChitchat,
    isCompletedForRating: baseHook.isCompletedForRating,
    setIsCompletedForRating: baseHook.setIsCompletedForRating,
    isWaitingForStreaming: baseHook.isWaitingForStreaming,
    isStreamingEnd: baseHook.isStreamingEnd,
    setIsStreamingEnd: baseHook.setIsStreamingEnd,
    missionJson: baseHook.missionJson,
    setMissionJson: baseHook.setMissionJson
  }
}

export const CONSULTATION_LIST_DEFAULT_LIMIT = 20 // 상담 목록 조회 기본 페이지 크기
export const CONSULTATION_CHAT_MESSAGE_LIST_DEFAULT_LIMIT = 10 // 상담 메시지 목록 조회 기본 페이지 크기

/* 상담 목록 조회 */
export const useGetConsultationList = () => {
  return useInfiniteQuery({
    queryKey: ['consultationList', CONSULTATION_LIST_DEFAULT_LIMIT],
    queryFn: ({ pageParam = 0 }) =>
      getConsultationListApi({ offset: pageParam, limit: CONSULTATION_LIST_DEFAULT_LIMIT }),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage && lastPage.length === CONSULTATION_LIST_DEFAULT_LIMIT
        ? allPages.length * CONSULTATION_LIST_DEFAULT_LIMIT
        : undefined
    },
    initialPageParam: 0,
    select: data => {
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
      const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)

      const grouped: {
        today: ConsultationListItemState[]
        last7Days: ConsultationListItemState[]
        last30Days: ConsultationListItemState[]
        previous: ConsultationListItemState[]
      } = {
        today: [],
        last7Days: [],
        last30Days: [],
        previous: []
      }

      // 모든 페이지의 데이터를 하나로 합치기
      const allItems = data.pages.flat()

      allItems.forEach(item => {
        const itemDate = new Date(item.created_at)
        const itemDateOnly = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate())

        if (itemDateOnly.getTime() === today.getTime()) {
          grouped.today.push({ id: item.id.toString(), title: item.title, status: item.status, type: item.type })
        } else if (itemDateOnly >= sevenDaysAgo) {
          grouped.last7Days.push({ id: item.id.toString(), title: item.title, status: item.status, type: item.type })
        } else if (itemDateOnly >= thirtyDaysAgo) {
          grouped.last30Days.push({ id: item.id.toString(), title: item.title, status: item.status, type: item.type })
        } else {
          grouped.previous.push({ id: item.id.toString(), title: item.title, status: item.status, type: item.type })
        }
      })

      return grouped
    }
  })
}

/* 상담 메시지 목록 조회 */
export const useGetConsultationChatMessageList = (consultation_id: number) => {
  return useInfiniteQuery({
    queryKey: ['consultationChatMessageList', consultation_id, CONSULTATION_CHAT_MESSAGE_LIST_DEFAULT_LIMIT],
    enabled: !!consultation_id && consultation_id !== 0,
    queryFn: ({ pageParam = 0 }) =>
      getConsultationChatMessageListApi(consultation_id, {
        offset: pageParam,
        limit: CONSULTATION_CHAT_MESSAGE_LIST_DEFAULT_LIMIT
      }),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage && lastPage.messages.length === CONSULTATION_CHAT_MESSAGE_LIST_DEFAULT_LIMIT
        ? allPages.length * CONSULTATION_CHAT_MESSAGE_LIST_DEFAULT_LIMIT
        : undefined
    },
    initialPageParam: 0,
    select: data => {
      // 모든 페이지의 메시지를 하나로 합치고 시간순으로 정렬 (최신이 아래)
      const allMessages = data.pages.flatMap(page => page.messages).reverse()

      // 날짜별로 그룹화
      const groupedMessages: Array<{
        date: string
        messagesList: Array<{
          id: string
          text: string
          sender: 'user' | 'ai'
          timestamp: string
        }>
      }> = []

      allMessages.forEach(apiMsg => {
        const messageDate = new Date(apiMsg.created_at)
          .toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })
          .replace(/\./g, '')
          .replace(/\s/g, '')

        const chatMessage = {
          id: apiMsg.id.toString(),
          text: apiMsg.content,
          sender: apiMsg.role_name === 'user' ? ('user' as const) : ('ai' as const),
          timestamp: new Date(apiMsg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }

        const existingGroup = groupedMessages.find(group => group.date === messageDate)
        if (existingGroup) {
          existingGroup.messagesList.push(chatMessage)
        } else {
          groupedMessages.push({ date: messageDate, messagesList: [chatMessage] })
        }
      })

      return {
        ...data,
        groupedMessages,
        isFirstPage: data.pages.length === 1
      }
    }
  })
}

/* 최근 블럭 스텝 조회 */
export const useGetMyStepsByLatestBlock = (enabled = true) => {
  return useQuery({
    queryKey: ['myStepsByLatestBlock'],
    queryFn: () => getMyStepsByLatestBlockApi(),
    enabled
  })
}

/* 상담 상세 조회 */
export const useGetConsultationDetail = (consultation_id: number) => {
  return useQuery({
    queryKey: ['consultationDetail', consultation_id],
    queryFn: () => getConsultationDetailApi(consultation_id),
    enabled: !!consultation_id && typeof consultation_id === 'number' && consultation_id !== 0
  })
}

/* 진행 중인 상담 조회 */
export const useGetActiveConsultation = () => {
  return useQuery({
    queryKey: ['activeConsultation'],
    queryFn: () => getActiveConsultationApi(),
    enabled: true
  })
}
